
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const DesertPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/8ba86b607b89269085fa1d43daed6cda.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Desert Oasis</h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            Discover the mystical beauty of the desert in our luxury retreats, where endless skies meet dramatic landscapes
          </p>
          <Link 
            to="/book" 
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            Book Desert Experience
          </Link>
        </div>
      </section>

      {/* Experience Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Desert Majesty</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our desert retreats offer an otherworldly experience where vast landscapes and dramatic rock formations 
                create a backdrop for profound connection with nature. Experience the silence and solitude that only 
                the desert can provide, along with some of the world's most spectacular stargazing opportunities.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Each desert location features climate-controlled luxury accommodations designed to provide comfort in 
                extreme environments, with panoramic views of endless horizons and unique desert ecosystems.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">300+</div>
                  <div className="text-gray-600">Clear Sky Days</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">8</div>
                  <div className="text-gray-600">Desert Locations</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20desert%20glamping%20tent%20with%20panoramic%20desert%20views%2C%20modern%20climate-controlled%20accommodation%20with%20large%20windows%2C%20private%20terrace%20overlooking%20red%20rock%20formations%2C%20premium%20desert%20architecture%20blending%20with%20natural%20landscape%2C%20sustainable%20design%20for%20extreme%20environments&width=600&height=400&seq=desert-glamp&orientation=landscape"
                alt="Desert Glamping"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-20 bg-orange-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Desert Activities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From sunrise meditation to stargazing adventures, the desert offers unique experiences found nowhere else
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-star-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Stargazing</h3>
              <p className="text-gray-700 leading-relaxed">
                Experience world-class astronomy with professional telescopes and expert guides under some of the darkest skies on Earth.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-compass-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Desert Exploration</h3>
              <p className="text-gray-700 leading-relaxed">
                Discover hidden canyons, ancient petroglyphs, and unique desert wildlife with experienced desert guides and naturalists.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-sun-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Sunrise Meditation</h3>
              <p className="text-gray-700 leading-relaxed">
                Begin each day with peaceful meditation sessions as the desert awakens with spectacular sunrise colors and desert sounds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Locations */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Desert Locations</h2>
            <p className="text-xl text-gray-600">Discover our most spectacular desert retreat destinations</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Sonoran%20desert%20landscape%20with%20luxury%20glamping%20resort%2C%20dramatic%20saguaro%20cacti%20and%20desert%20mountains%2C%20golden%20sunset%20lighting%2C%20premium%20eco-lodge%20accommodation%20blending%20with%20desert%20environment%2C%20sustainable%20architecture&width=600&height=300&seq=sonoran-desert&orientation=landscape"
                alt="Sonoran Desert Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Sonoran Sanctuary</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Experience the iconic beauty of the Sonoran Desert with towering saguaro cacti and stunning mountain vistas.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Arizona, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-orange-600 hover:text-orange-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Mojave%20desert%20with%20luxury%20glamping%20site%2C%20dramatic%20rock%20formations%20and%20Joshua%20trees%2C%20vast%20open%20desert%20landscape%2C%20premium%20eco-accommodation%20with%20panoramic%20desert%20views%2C%20sustainable%20design%20harmonizing%20with%20environment&width=600&height=300&seq=mojave-desert&orientation=landscape"
                alt="Mojave Desert Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mojave Oasis</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Discover the otherworldly beauty of the Mojave Desert with unique Joshua tree forests and dramatic rock formations.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>California, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-orange-600 hover:text-orange-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-orange-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Embrace the Desert Spirit</h2>
          <p className="text-xl text-orange-100 mb-8 leading-relaxed">
            Book your desert retreat today and discover the profound beauty and tranquility of these ancient landscapes
          </p>
          <Link 
            to="/book" 
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
          >
            Book Your Desert Retreat
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesertPage;
